/**
 * TRHC Gift Ladder: adds, swaps, auto-adds and auto-removes free gifts in the Horizon cart drawer,
 * opens the gift options sheet, and animates the unlock moments.
 * Markup: snippets/trhc-gift-ladder.liquid + snippets/trhc-gift-tier.liquid
 * It fires the same CartLinesUpdateEvent Horizon's own add-to-cart uses, so the drawer
 * re-renders from the server after every change (no client-side guessing).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const SECTION_ID = 'cart-drawer-section';
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const AUTO_KEY = 'trhc-gift-auto';
const FILL_KEY = 'trhc-gl-fill';
const REACHED_KEY = 'trhc-gl-reached';
const MSG_KEY = 'trhc-gl-msg';
let inFlight = false;

const store = {
  get(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {}
  },
};

/* Tiers whose single gift was already auto-added this visit. If the shopper removes it, we don't force it back;
   the "Add my ..." button stays available. Cleared when the tier locks again (cart drops below the threshold). */
function readAuto() {
  try {
    return JSON.parse(store.get(AUTO_KEY) || '{}');
  } catch (e) {
    return {};
  }
}
function writeAuto(map) {
  store.set(AUTO_KEY, JSON.stringify(map));
}

async function postJSON(path, data) {
  const res = await fetch(ROOT + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok || json.status) throw new Error(json.description || json.message || 'Cart request failed');
  return json;
}

async function runCartChange(source, event, work) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ ...event, context: 'cart', promise: deferred.promise }));
  try {
    const sections = await work();
    const cart = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: sections || undefined,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'trhc-gift-ladder',
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/* Pricing comes from Rebuy's "FREE GIFT" automatic discount (a Shopify discount Function), not from us.
   It makes ONE unit of any line carrying these three properties free, and it does not check the cart total,
   so this file + data-stale-keys are what enforce the thresholds. Widget 299529 = Rebuy's free-gift widget.
   Override per page with <gift-ladder data-rebuy-widget="...">. */
const REBUY_WIDGET_DEFAULT = '299529';

function giftProps(tier) {
  const el = document.querySelector('gift-ladder[data-rebuy-widget]');
  const widget = (el && el.dataset.rebuyWidget) || REBUY_WIDGET_DEFAULT;
  return { _gift_tier: String(tier), _source: 'Rebuy', _widget_id: widget, _attribution: 'Rebuy Cart' };
}

function addGift(source, variantId, tier, replaceKey) {
  return runCartChange(
    source,
    { action: 'add', lines: [{ merchandiseId: variantId, quantity: 1 }] },
    async () => {
      if (replaceKey) await postJSON('cart/change.js', { id: replaceKey, quantity: 0 });
      const res = await postJSON('cart/add.js', {
        items: [{ id: variantId, quantity: 1, properties: giftProps(tier) }],
        sections: SECTION_ID,
      });
      return res.sections;
    }
  );
}

/* ---------- Gift options sheet ---------- */
let openSheet = null;
let lastFocus = null;

/* The sheet's markup lives inside the scrolling item list. On iPhone a position:fixed element inside a scroller is
   clipped by it and painted under the fixed checkout footer, and swipes scroll the list behind it. So while it's open
   we move the sheet up to the drawer <dialog> itself (a placeholder marks its home), lock the list's scroll, and put
   it back (or drop it, if the drawer re-rendered meanwhile) when it closes. */
const sheetHome = new WeakMap();
const lockList = (on) => document.querySelectorAll('.cart-drawer__items').forEach((el) => el.classList.toggle('gl-scroll-locked', on));

function showSheet(sheet) {
  if (!sheet) return;
  lastFocus = document.activeElement;
  const host = sheet.closest('dialog') || document.body;
  if (sheet.parentElement !== host) {
    const mark = document.createComment('gl-sheet-home');
    sheet.before(mark);
    sheetHome.set(sheet, mark);
    host.appendChild(sheet);
  }
  lockList(true);
  sheet.hidden = false;
  openSheet = sheet;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => sheet.classList.add('is-open'));
  });
  const close = sheet.querySelector('.gl-sheet__close');
  if (close) close.focus({ preventScroll: true });
}

function hideSheet(sheet) {
  if (!sheet || sheet.hidden) return;
  sheet.classList.remove('is-open');
  if (openSheet === sheet) openSheet = null;
  lockList(false);
  setTimeout(() => {
    if (sheet.classList.contains('is-open')) return;
    sheet.hidden = true;
    const mark = sheetHome.get(sheet);
    if (mark && mark.isConnected) mark.replaceWith(sheet);
    else if (mark) sheet.remove(); // the drawer re-rendered while it was open: a fresh copy already exists
    sheetHome.delete(sheet);
  }, 320);
  if (lastFocus && lastFocus.isConnected) lastFocus.focus({ preventScroll: true });
}

function pickOption(opt) {
  const sheet = opt.closest('.gl-sheet');
  if (!sheet) return;
  sheet.querySelectorAll('[data-gl-opt]').forEach((o) => o.setAttribute('aria-checked', o === opt ? 'true' : 'false'));
  const name = sheet.querySelector('[data-gl-name]');
  if (name) name.textContent = opt.dataset.name;
  const big = sheet.querySelector('[data-gl-big]');
  if (big && opt.dataset.img && big.getAttribute('src') !== opt.dataset.img) {
    big.classList.add('is-swapping');
    const next = new Image();
    next.onload = next.onerror = () => {
      big.src = opt.dataset.img;
      big.alt = big.alt.replace(/\s\S+$/, '') + ' ' + opt.dataset.name;
      big.classList.remove('is-swapping');
    };
    next.src = opt.dataset.img;
  }
  const add = sheet.querySelector('[data-gl-add]');
  if (add) {
    add.dataset.variantId = opt.dataset.variantId;
    const isChosen = opt.dataset.chosen === '1';
    add.disabled = isChosen;
    add.textContent = isChosen ? 'Added' : add.dataset.labelAdd;
  }
}

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Empty cart "+ Add" (snippets/trhc-cart-empty.liquid): add one of the best sellers and re-render the drawer.
  const quick = target.closest('[data-trhc-quick-add]');
  if (quick) {
    event.preventDefault();
    if (quick.classList.contains('is-loading')) return;
    quick.classList.add('is-loading');
    const variantId = Number(quick.dataset.variantId);
    try {
      await runCartChange(quick, { action: 'add', lines: [{ merchandiseId: variantId, quantity: 1 }] }, async () => {
        const res = await postJSON('cart/add.js', { items: [{ id: variantId, quantity: 1 }], sections: SECTION_ID });
        return res.sections;
      });
    } catch (error) {
      console.warn('[gift-ladder] quick add failed:', error);
      quick.classList.remove('is-loading');
    }
    return;
  }

  // "Pick your FREE gift" nudges scroll the gift list to that gift's row and pulse it.
  const pick = target.closest('[data-gl-pick]');
  if (pick) {
    event.preventDefault();
    const tier = document.querySelector(`.cart-drawer__items .gl-tier[data-tier="${pick.dataset.glPick}"]`);
    if (tier) {
      tier.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
      play(tier, [{ backgroundColor: '#8cebf7' }, { backgroundColor: '#e3fafd' }], { duration: 900, delay: 300, easing: 'ease-out' });
    }
    return;
  }

  const opener = target.closest('[data-gl-open]');
  if (opener) {
    event.preventDefault();
    showSheet(document.getElementById(opener.dataset.glOpen));
    return;
  }
  if (target.closest('[data-gl-close]')) {
    event.preventDefault();
    hideSheet(target.closest('.gl-sheet'));
    return;
  }
  const opt = target.closest('[data-gl-opt]');
  if (opt) {
    event.preventDefault();
    pickOption(opt);
    return;
  }

  const button = target.closest('[data-gift-add]');
  if (!button || inFlight || button.disabled) return;
  event.preventDefault();

  inFlight = true;
  button.classList.add('is-loading');
  try {
    await addGift(button, Number(button.dataset.variantId), button.dataset.tier, button.dataset.replaceKey);
    if (openSheet) hideSheet(openSheet);
  } catch (error) {
    console.warn('[gift-ladder] add failed:', error);
    button.classList.remove('is-loading');
  } finally {
    inFlight = false;
  }
});

/* Checkout guard. Rebuy's discount doesn't check the cart total, so a shopper who removes a hat and taps Checkout
   before the drawer finishes sweeping would carry a gift they no longer qualify for. Before leaving for checkout we
   ask the server for a fresh render of the drawer (the same Liquid that decides data-stale-keys) and remove any
   gift that no longer qualifies. Costs one quick request on the Checkout tap. */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
document.addEventListener('click', async (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-gl-checkout]') : null;
  if (!link) return;
  event.preventDefault();
  if (link.classList.contains('is-loading')) return;
  link.classList.add('is-loading');
  try {
    for (let i = 0; inFlight && i < 20; i++) await sleep(150);
    const res = await fetch(ROOT + '?sections=' + SECTION_ID, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const html = (await res.json())[SECTION_ID] || '';
    const ladder = new DOMParser().parseFromString(html, 'text/html').querySelector('gift-ladder');
    const stale = ((ladder && ladder.dataset.staleKeys) || '').split(',').filter(Boolean);
    if (stale.length) {
      await postJSON('cart/update.js', { updates: Object.fromEntries(stale.map((key) => [key, 0])) });
    }
  } catch (error) {
    console.warn('[gift-ladder] checkout check failed:', error);
  }
  window.location.href = link.getAttribute('href') || ROOT + 'checkout';
});

// Esc closes the sheet first (instead of the whole cart drawer).
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key === 'Escape' && openSheet && !openSheet.hidden) {
      event.preventDefault();
      event.stopPropagation();
      hideSheet(openSheet);
    }
  },
  true
);

/* ---------- Unlock animations ----------
   Web Animations API (not CSS classes): Horizon morphs the drawer more than once per cart change,
   which would wipe a class, but leaves running element animations alone. */
const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function play(el, keyframes, options) {
  if (!el || !el.animate || reduceMotion()) return;
  try {
    el.animate(keyframes, { fill: 'none', ...options });
  } catch (e) {}
}

function celebrateTier(scope, ladder, tier) {
  const pill = scope.querySelector(`.gl-meter__pill[data-tier="${tier}"]`);
  if (pill) {
    const base = pill.style.transform || 'translate(-50%, -50%)';
    play(pill, [{ transform: `${base} scale(0.8)` }, { transform: `${base} scale(1.18)`, offset: 0.55 }, { transform: `${base} scale(1)` }], { duration: 650, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' });
    play(pill, [{ boxShadow: '0 0 0 0 rgba(17,17,17,0.5)' }, { boxShadow: '0 0 0 14px rgba(17,17,17,0)' }], { duration: 900, easing: 'ease-out' });
  }
  const block = ladder.querySelector(`.gl-tier[data-tier="${tier}"]`);
  if (block) {
    play(block, [{ backgroundColor: '#8cebf7' }, { backgroundColor: '#e3fafd' }], { duration: 1000, easing: 'ease-out' });
    play(block.querySelector('.gl-tier__status'), [{ transform: 'scale(0.7)', opacity: 0 }, { transform: 'scale(1.08)', opacity: 1, offset: 0.6 }, { transform: 'scale(1)' }], { duration: 450, delay: 120, easing: 'ease-out' });
    block.querySelectorAll('.gl-card').forEach((card, i) =>
      play(card, [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 380, delay: 80 + i * 60, easing: 'ease-out', fill: 'backwards' })
    );
  }
  if (tier === '1') {
    const ship = scope.querySelector('.gl-checkout__row.is-ship span:last-child');
    if (ship) {
      ship.style.display = 'inline-block';
      play(ship, [{ transform: 'scale(0.8)' }, { transform: 'scale(1.15)', offset: 0.6 }, { transform: 'scale(1)' }], { duration: 500, delay: 150, easing: 'ease-out' });
    }
  }
}

function animateMeter(ladder) {
  const scope = ladder.closest('.cart-drawer__inner, dialog, body') || document;
  const fill = scope.querySelector('.gl-meter__fill');
  if (fill) {
    const now = fill.dataset.fill || '0';
    const prev = store.get(FILL_KEY);
    if (prev !== null && prev !== now) {
      play(fill, [{ width: prev + '%' }, { width: now + '%' }], { duration: 700, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
    }
    store.set(FILL_KEY, now);
  }

  const msg = scope.querySelector('.gl-meter__msg');
  if (msg) {
    const text = msg.textContent.trim();
    const prevMsg = store.get(MSG_KEY);
    if (prevMsg !== null && prevMsg !== text) {
      play(msg, [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: 350, easing: 'ease-out' });
    }
    store.set(MSG_KEY, text);
  }

  const unlocked = (ladder.dataset.unlocked || '').split(',').filter(Boolean);
  const prevRaw = store.get(REACHED_KEY);
  if (prevRaw !== null) {
    const prevReached = prevRaw.split(',').filter(Boolean);
    unlocked.filter((tier) => !prevReached.includes(tier)).forEach((tier) => celebrateTier(scope, ladder, tier));
  }
  store.set(REACHED_KEY, unlocked.join(','));
}

/**
 * Removes gifts whose tier is no longer unlocked (data-stale-keys), then auto-adds the gift for an
 * unlocked single-option tier like the mystery hat (data-auto-add = "variant_id:tier").
 */
class GiftLadder extends HTMLElement {
  static observedAttributes = ['data-stale-keys', 'data-auto-add', 'data-unlocked', 'data-q'];
  #lastSweep = '';
  #lastAuto = '';
  #queued = false;

  connectedCallback() {
    this.#run();
  }

  attributeChangedCallback() {
    this.#run();
  }

  #run() {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      setTimeout(() => {
        try {
          if (this.isConnected) animateMeter(this);
        } catch (e) {}
      }, 60);
      this.#work();
    });
  }

  async #work() {
    if (inFlight) {
      setTimeout(() => this.#work(), 300);
      return;
    }

    // Forget auto-adds for tiers that locked again, so they auto-add next time the tier unlocks.
    const unlocked = (this.dataset.unlocked || '').split(',').filter(Boolean);
    const auto = readAuto();
    let changed = false;
    for (const tier of Object.keys(auto)) {
      if (!unlocked.includes(tier)) {
        delete auto[tier];
        changed = true;
      }
    }
    if (changed) writeAuto(auto);

    const staleRaw = this.dataset.staleKeys || '';
    const keys = staleRaw.split(',').filter(Boolean);
    if (!keys.length) this.#lastSweep = '';
    if (keys.length && staleRaw !== this.#lastSweep) {
      this.#lastSweep = staleRaw;
      inFlight = true;
      try {
        await runCartChange(
          this,
          { action: 'remove', lines: keys.map((id) => ({ id, quantity: 0 })) },
          async () => {
            const updates = Object.fromEntries(keys.map((key) => [key, 0]));
            const res = await postJSON('cart/update.js', { updates, sections: SECTION_ID });
            return res.sections;
          }
        );
      } catch (error) {
        console.warn('[gift-ladder] cleanup failed:', error);
      } finally {
        inFlight = false;
      }
      return; // the drawer re-renders; auto-add runs on the fresh markup
    }

    const autoRaw = this.dataset.autoAdd || '';
    if (!autoRaw) {
      this.#lastAuto = '';
      return;
    }
    const [variantId, tier] = autoRaw.split(':');
    if (!variantId || !tier || autoRaw === this.#lastAuto || auto[tier]) return;

    this.#lastAuto = autoRaw;
    auto[tier] = variantId;
    writeAuto(auto);
    const btn = this.querySelector(`[data-gift-add][data-variant-id="${variantId}"]`);
    if (btn) {
      btn.classList.add('is-loading');
      // Image buttons (the gift photo) keep their photo; only text buttons get the label
      if (!btn.classList.contains('gl-card__img')) btn.textContent = 'Adding your FREE gift';
    }
    inFlight = true;
    try {
      await addGift(this, Number(variantId), tier, '');
    } catch (error) {
      console.warn('[gift-ladder] auto-add failed:', error);
    } finally {
      inFlight = false;
    }
  }
}

if (!customElements.get('gift-ladder')) {
  customElements.define('gift-ladder', GiftLadder);
}
